/**
 * Preview pane for the plugin builder.
 * Renders the built plugin in a sandboxed iframe with device sizing,
 * undo/redo controls, graph, code, and log tabs.
 */

import { useState, useRef, useCallback, useEffect } from 'react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import {
  Loader2,
  RefreshCw,
  Code2,
  Eye,
  AlertCircle,
  Undo2,
  Redo2,
  Monitor,
  Tablet,
  Smartphone,
  Sun,
  Moon,
  GitBranch,
  ScrollText,
} from 'lucide-react';
import { CodeViewerPane } from './CodeViewerPane';
import { GraphPane } from './GraphPane';
import { LogContent } from './LogContent';
import type { VirtualFileSystem, ProcessGraph, LogEntry, AiConfig } from '../types';
import type { ModelEntity } from '@/lib/api';

type DeviceSize = 'desktop' | 'tablet' | 'mobile';
type Tab = 'preview' | 'graph' | 'code' | 'log';

const DEVICE_WIDTHS: Record<DeviceSize, string> = {
  desktop: '100%',
  tablet: '768px',
  mobile: '375px',
};

interface PluginPreviewPaneProps {
  previewHtml: string | null;
  files: VirtualFileSystem | null;
  isBuildingPreview: boolean;
  buildError: string | null;
  onBuildPreview: () => Promise<string | null>;
  // Undo/redo
  onUndo?: () => void;
  onRedo?: () => void;
  canUndo?: boolean;
  canRedo?: boolean;
  undoCount?: number;
  redoCount?: number;
  // Admin controls
  verbose?: boolean;
  onVerboseChange?: (value: boolean) => void;
  deepThinking?: boolean;
  onDeepThinkingChange?: (value: boolean) => void;
  isAdmin?: boolean;
  // Graph
  processGraph?: ProcessGraph;
  currentNodeId?: string | null;
  aiConfigs?: AiConfig[];
  /** Model definitions for resolving display names in graph */
  modelDefinitions?: ModelEntity[];
  // Logs
  logEntries?: LogEntry[];
  onClearLogs?: () => void;
  // Screenshots
  onScreenshotCaptured?: (dataUrl: string) => void;
  // Preview theme
  previewTheme?: 'light' | 'dark';
  onPreviewThemeToggle?: () => void;
  // Navigate to node callback (exposed for parent wiring)
  onNavigateToNode?: (nodeId: string) => void;
  // Ref to the preview iframe — owned by the parent so it can postMessage to the iframe
  iframeRef: React.RefObject<HTMLIFrameElement | null>;
  /** Bridge ref for sending bridge-protocol messages (theme, step navigation). */
  bridgeRef?: React.RefObject<import('../../../components/PluginBridge').PluginBridgeHost | null>;
  /** Called when the preview iframe finishes loading (for bridge initialization). */
  onPreviewIframeLoad?: () => void;
  className?: string;
}

export function PluginPreviewPane({
  previewHtml,
  files,
  isBuildingPreview,
  buildError,
  onBuildPreview,
  onUndo,
  onRedo,
  canUndo = false,
  canRedo = false,
  undoCount = 0,
  redoCount = 0,
  verbose = false,
  onVerboseChange,
  deepThinking = false,
  onDeepThinkingChange,
  isAdmin = false,
  processGraph,
  currentNodeId,
  aiConfigs,
  modelDefinitions,
  logEntries = [],
  onClearLogs,
  onScreenshotCaptured,
  previewTheme,
  onPreviewThemeToggle,
  iframeRef,
  bridgeRef,
  onPreviewIframeLoad,
  onNavigateToNode,
  className,
}: PluginPreviewPaneProps) {
  const [activeTab, setActiveTab] = useState<Tab>('preview');
  const [deviceSize, setDeviceSize] = useState<DeviceSize>('desktop');
  const onScreenshotCapturedRef = useRef(onScreenshotCaptured);
  onScreenshotCapturedRef.current = onScreenshotCaptured;
  const fileCount = files ? Object.keys(files).length : 0;
  const hasGraph = processGraph && processGraph.nodes.length > 0;

  // Navigate to a specific node's view in the preview iframe
  const navigateToNode = useCallback(
    (nodeId: string) => {
      setActiveTab('preview');
      bridgeRef?.current?.navigateToStep(nodeId);
      onNavigateToNode?.(nodeId);
    },
    [bridgeRef, onNavigateToNode],
  );

  // Listen for postMessage events from the iframe
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (!event.data || typeof event.data !== 'object') return;

      if (event.data.type === 'SCREENSHOT_RESULT' && event.data.dataUrl) {
        onScreenshotCapturedRef.current?.(event.data.dataUrl);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  // Send theme to iframe via bridge protocol (avoids srcDoc replacement which reloads the iframe)
  useEffect(() => {
    if (!previewHtml) return;
    bridgeRef?.current?.sendThemeUpdate();
  }, [previewTheme, previewHtml, bridgeRef]);

  // Trigger screenshot capture and bridge init after preview loads
  const handleIframeLoad = useCallback(() => {
    if (!iframeRef.current?.contentWindow) return;
    // Initialize bridge first, then capture screenshot after render settles
    onPreviewIframeLoad?.();
    if (onScreenshotCapturedRef.current) {
      setTimeout(() => {
        iframeRef.current?.contentWindow?.postMessage({ type: 'CAPTURE_SCREENSHOT' }, '*');
      }, 500);
    }
  }, [onPreviewIframeLoad]);

  // Available tabs
  const tabs: Array<{
    key: Tab;
    label: string;
    icon: React.ElementType;
    badge?: number;
    adminOnly?: boolean;
  }> = [
    { key: 'preview', label: 'Preview', icon: Eye },
    ...(hasGraph ? [{ key: 'graph' as Tab, label: 'Graph', icon: GitBranch }] : []),
    {
      key: 'code',
      label: 'Code',
      icon: Code2,
      badge: fileCount > 0 ? fileCount : undefined,
      adminOnly: true,
    },
    {
      key: 'log',
      label: 'Log',
      icon: ScrollText,
      badge: logEntries.length > 0 ? logEntries.length : undefined,
      adminOnly: true,
    },
  ];

  const visibleTabs = tabs.filter((t) => !t.adminOnly || isAdmin);

  return (
    <div className={cn('flex flex-col h-full', className)}>
      {/* Tab bar + controls */}
      <div className="flex items-center border-b bg-muted/20 px-3 py-1.5 gap-1 flex-shrink-0 flex-wrap min-h-[41px]">
        {/* Tabs */}
        {visibleTabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={cn(
              'flex items-center gap-1.5 text-xs font-medium px-3 py-1 rounded-md transition-colors',
              activeTab === tab.key
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground',
            )}
          >
            <tab.icon className="h-3.5 w-3.5" />
            {tab.label}
            {tab.badge != null && (
              <span className="text-[10px] px-1 rounded-full bg-muted text-muted-foreground">
                {tab.badge}
              </span>
            )}
          </button>
        ))}

        {/* Spacer */}
        <div className="flex-1" />

        {/* Device selector (preview tab only, hidden on small screens) */}
        {activeTab === 'preview' && (
          <div className="hidden 2xl:flex items-center gap-0.5 mr-2">
            {[
              { size: 'desktop' as DeviceSize, icon: Monitor },
              { size: 'tablet' as DeviceSize, icon: Tablet },
              { size: 'mobile' as DeviceSize, icon: Smartphone },
            ].map(({ size, icon: Icon }) => (
              <Button
                key={size}
                variant={deviceSize === size ? 'default' : 'ghost'}
                size="icon"
                className="h-7 w-7"
                onClick={() => setDeviceSize(size)}
                title={size}
              >
                <Icon className="h-3.5 w-3.5" />
              </Button>
            ))}
          </div>
        )}

        {/* Undo/Redo */}
        {activeTab === 'preview' && onUndo && onRedo && (
          <div className="flex items-center gap-0.5 mr-2">
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 relative"
              onClick={onUndo}
              disabled={!canUndo}
              title={`Undo (${undoCount})`}
            >
              <Undo2 className="h-3.5 w-3.5" />
              {undoCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 text-[9px] bg-primary text-primary-foreground rounded-full h-3.5 min-w-[14px] flex items-center justify-center">
                  {undoCount}
                </span>
              )}
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 relative"
              onClick={onRedo}
              disabled={!canRedo}
              title={`Redo (${redoCount})`}
            >
              <Redo2 className="h-3.5 w-3.5" />
              {redoCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 text-[9px] bg-primary text-primary-foreground rounded-full h-3.5 min-w-[14px] flex items-center justify-center">
                  {redoCount}
                </span>
              )}
            </Button>
          </div>
        )}

        {/* Admin: Deep thinking + Verbose */}
        {isAdmin && (
          <div className="flex items-center gap-3 mr-2">
            {onDeepThinkingChange && (
              <div className="flex items-center gap-1.5">
                <Checkbox
                  id="deep-thinking"
                  checked={deepThinking}
                  onCheckedChange={(checked) => onDeepThinkingChange(checked === true)}
                />
                <Label htmlFor="deep-thinking" className="text-xs cursor-pointer">
                  Deep
                </Label>
              </div>
            )}
            {onVerboseChange && (
              <div className="flex items-center gap-1.5">
                <Checkbox
                  id="verbose-mode"
                  checked={verbose}
                  onCheckedChange={(checked) => onVerboseChange(checked === true)}
                />
                <Label htmlFor="verbose-mode" className="text-xs cursor-pointer">
                  Verbose
                </Label>
              </div>
            )}
          </div>
        )}

        {/* Build button */}
        {activeTab === 'preview' && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onBuildPreview()}
            disabled={isBuildingPreview || !files || fileCount === 0}
            className="h-7 text-xs"
          >
            {isBuildingPreview ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />
            ) : (
              <RefreshCw className="h-3.5 w-3.5 mr-1" />
            )}
            Build
          </Button>
        )}

        {/* Preview theme toggle — far right */}
        {activeTab === 'preview' && onPreviewThemeToggle && (
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={onPreviewThemeToggle}
            title={`Preview theme: ${previewTheme || 'light'}`}
          >
            {previewTheme === 'dark' ? (
              <Sun className="h-3.5 w-3.5" />
            ) : (
              <Moon className="h-3.5 w-3.5" />
            )}
          </Button>
        )}
      </div>

      {/* Content — Preview is always mounted (hidden via CSS) to preserve iframe state */}
      <div className="flex-1 min-h-0 bg-background relative">
        <div
          className={
            activeTab === 'preview' ? 'h-full' : 'absolute inset-0 invisible pointer-events-none'
          }
        >
          <PreviewContent
            previewHtml={previewHtml}
            isBuildingPreview={isBuildingPreview}
            buildError={buildError}
            fileCount={fileCount}
            deviceWidth={DEVICE_WIDTHS[deviceSize]}
            iframeRef={iframeRef}
            onIframeLoad={handleIframeLoad}
          />
        </div>
        {activeTab === 'graph' ? (
          <GraphPane
            processGraph={processGraph}
            currentNodeId={currentNodeId}
            aiConfigs={aiConfigs}
            modelDefinitions={modelDefinitions}
            onPreviewNode={navigateToNode}
          />
        ) : activeTab === 'code' ? (
          <CodeViewerPane files={files} className="h-full" />
        ) : activeTab === 'log' ? (
          <LogContent entries={logEntries} onClear={onClearLogs ?? (() => {})} />
        ) : null}
      </div>
    </div>
  );
}

function PreviewContent({
  previewHtml,
  isBuildingPreview,
  buildError,
  fileCount,
  deviceWidth,
  iframeRef,
  onIframeLoad,
}: {
  previewHtml: string | null;
  isBuildingPreview: boolean;
  buildError: string | null;
  fileCount: number;
  deviceWidth: string;
  iframeRef: React.RefObject<HTMLIFrameElement | null>;
  onIframeLoad: () => void;
}) {
  if (isBuildingPreview) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-2 text-muted-foreground">
        <Loader2 className="h-8 w-8 animate-spin" />
        <span className="text-sm">Building preview...</span>
      </div>
    );
  }

  if (buildError) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-3 p-6 text-center">
        <AlertCircle className="h-8 w-8 text-destructive" />
        <div>
          <h4 className="text-sm font-semibold text-destructive mb-1">Build Error</h4>
          <pre className="text-xs text-muted-foreground whitespace-pre-wrap max-w-md">
            {buildError}
          </pre>
        </div>
      </div>
    );
  }

  if (!previewHtml) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-2 text-muted-foreground">
        <Eye className="h-8 w-8 opacity-30" />
        <span className="text-sm">
          {fileCount > 0 ? 'Click "Build" to preview your plugin' : 'No files to preview yet'}
        </span>
      </div>
    );
  }

  return (
    <div className="flex justify-center h-full bg-muted/30">
      <iframe
        ref={iframeRef}
        srcDoc={previewHtml}
        className="h-full border-0 bg-background transition-all"
        style={{ width: deviceWidth, maxWidth: '100%' }}
        sandbox="allow-scripts allow-same-origin"
        title="Plugin Preview"
        onLoad={onIframeLoad}
      />
    </div>
  );
}
